/**
 * SyncToCurrentVersionButton (Vincent #171, Eric 2026-05-25)
 *
 * Bouton qui exécute les migrations de représentation pour l'org appelante.
 * ZERO crédit IA consommé — c'est de la re-transformation pure.
 *
 * Affiche :
 *  - un badge « ✓ à jour » si tout est appliqué,
 *  - un badge « N à synchroniser » si des migrations sont en attente.
 *
 * Réservé aux fondateurs / admins.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowsClockwise, CheckCircle, Warning, SpinnerGap } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { useLang } from "../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SyncToCurrentVersionButton({ onSynced }) {
  const { lang } = useLang();
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/admin/sync-to-current-version/status`,
        { withCredentials: true }
      );
      setStatus(data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleRun = async () => {
    if (running) return;
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/sync-to-current-version`,
        {},
        { withCredentials: true, timeout: 180000 }
      );
      const totals = data.totals || {};
      const msg =
        lang === "fr"
          ? `Synchronisation OK : ${totals.ran} migration(s) appliquée(s), ${totals.errors} erreur(s). (0 crédit IA consommé)`
          : `Sync OK: ${totals.ran} migration(s) applied, ${totals.errors} error(s). (0 AI credit used)`;
      if (totals.errors > 0) toast.warning(msg);
      else toast.success(msg);
      await loadStatus();
      if (onSynced) onSynced(data);
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          (lang === "fr" ? "Échec de la synchronisation" : "Sync failed")
      );
    } finally {
      setRunning(false);
    }
  };

  if (!status) return null;

  const pending = status.pending || 0;
  const upToDate = pending === 0;

  return (
    <Button
      onClick={handleRun}
      disabled={running}
      variant="outline"
      className="flex items-center gap-2"
      data-testid="btn-sync-to-current-version"
      title={
        lang === "fr"
          ? "Réapplique tous les standards de représentation actuels aux données existantes. Aucun crédit IA consommé."
          : "Re-applies the current representation standards to existing data. No AI credit used."
      }
    >
      {running ? (
        <SpinnerGap size={16} className="animate-spin" />
      ) : upToDate ? (
        <CheckCircle size={18} className="text-teal-600" weight="fill" />
      ) : (
        <Warning size={18} className="text-amber-600" weight="fill" />
      )}
      <span>
        {running
          ? lang === "fr"
            ? "Synchronisation…"
            : "Syncing…"
          : upToDate
          ? lang === "fr"
            ? "Données à jour"
            : "Data up-to-date"
          : lang === "fr"
          ? `${pending} à synchroniser`
          : `${pending} pending sync`}
      </span>
      <ArrowsClockwise size={14} className="text-zinc-400 ml-1" />
    </Button>
  );
}
